import React, {
  useState, useEffect, useMemo, useCallback, useContext,
} from 'react';
import './WildClub.less';
import * as api from 'util/api';
import {
  LoadingOutlined,
  TeamOutlined,
  UserAddOutlined,
  CloseOutlined,
  ReloadOutlined,
  DownloadOutlined,
  InboxOutlined,
  PauseCircleOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  StopOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { DinoLoader, StickyHeader, CustomTable } from 'components';
import STORES from 'config/stores';
import { AuthContext } from 'util/Auth';
import { PermissionsContext } from 'util/PermissionsContext';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Spin,
  Table,
  Tag,
  Input,
  Tooltip,
  Alert,
  Switch,
} from 'antd';
import { CSVDownload } from 'react-csv';
import dayjs from 'dayjs';

const { Column } = Table;
const { Search } = Input;

const TABLE_ID = 'wild-club-members-table';
const COUNTRY_ID = STORES.GPNZ;
const BOX_PRICE = 450;

const formatNumber = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString();
};

const formatCurrency = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `$${Number(value).toLocaleString()}`;
};

const CustomSpinner = ({ fontSize = 24 }) => (
  <Spin
    indicator={(
      <LoadingOutlined
        style={{ fontSize }}
        spin
      />
)}
  />
);

const MetricStat = ({
  label, value, icon, loading,
}) => (
  <div className="wild-club-metric">
    <div className="wild-club-metric-icon">{icon}</div>
    <div className="wild-club-metric-text">
      <div className="wild-club-metric-value">
        {loading ? <CustomSpinner fontSize={18} /> : value}
      </div>
      <div className="wild-club-metric-label">{label}</div>
    </div>
  </div>
);

const statusTagColor = (status) => {
  const upper = String(status || '').toUpperCase();
  if (upper === 'ACTIVE') return 'green';
  if (upper === 'CANCELLED' || upper === 'EXPIRED') return 'red';
  return 'default';
};

const WildClub = () => {
  const history = useHistory();
  const { currentUser } = useContext(AuthContext);
  const { permissions } = useContext(PermissionsContext) || {};
  const canOpenDates = currentUser?.admin
    || (Array.isArray(permissions) && permissions.includes('webstore.wild-club-date-roll'));

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [downloadCsv, setDownloadCsv] = useState(false);
  const [excludeStaff, setExcludeStaff] = useState(true);

  const fetchData = useCallback((options = {}) => {
    setLoading(true);
    api.getWildClubInsights(COUNTRY_ID, { excludeStaff, ...options })
      .then((response) => {
        setData(response);
      })
      .catch((error) => {
        console.error('Error fetching Wild Club insights:', error);
        setData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [excludeStaff]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const metrics = data?.metrics || {};
  const progressMix = data?.progressMix || null;

  const memberRows = useMemo(() => {
    const rows = (data?.members || []).map((member) => ({
      ...member,
      key: member.subscriptionId || member.customerId,
    }));
    const query = searchQuery.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) => (
      String(row.customerName || '').toLowerCase().includes(query)
      || String(row.customerEmail || '').toLowerCase().includes(query)
      || String(row.subscriptionStatus || '').toLowerCase().includes(query)
      || String(row.nextReleaseTitle || '').toLowerCase().includes(query)
      || String(row.customerId || '').toLowerCase().includes(query)
    ));
  }, [data?.members, searchQuery]);

  const releaseRows = useMemo(() => {
    if (!data) return [];
    const rows = [...(data.releases || [])];
    if (data.dateTbc?.estimatedOrders > 0) {
      rows.push({ ...data.dateTbc, isDateTbc: true });
    }
    return rows.map((row) => ({
      ...row,
      key: row.isDateTbc ? 'date-tbc' : row.title,
    }));
  }, [data]);

  const handleDownload = useCallback(() => {
    setDownloadCsv(true);
    setTimeout(() => setDownloadCsv(false), 1000);
  }, []);

  const newMembersLabel = metrics.newMembersMonthLabel
    ? `New in ${metrics.newMembersMonthLabel}`
    : 'New this month';

  const nextConfirmedLabel = data?.nextConfirmedRelease
    ? `${data.nextConfirmedRelease.title} · ${formatNumber(data.nextConfirmedRelease.estimatedOrders)} orders`
    : 'None confirmed';

  if (loading && !data) {
    return <DinoLoader message="Loading Wild Club Insights..." />;
  }

  return (
    <div id="wild-club-container">
      <StickyHeader id="wild-club-header">
        <span className="wild-club-header-store">Garage Project NZ</span>
        <Tooltip title="Hide members with the Shopify customer tag Staff (default on)">
          <span className="wild-club-staff-toggle">
            <Switch
              size="small"
              checked={excludeStaff}
              onChange={setExcludeStaff}
              disabled={loading}
            />
            <span>Exclude staff</span>
            {excludeStaff && data?.staffExcluded > 0 && (
              <Tag color="default">{`${data.staffExcluded} hidden`}</Tag>
            )}
          </span>
        </Tooltip>
        <Button
          type="secondary"
          icon={<DownloadOutlined />}
          onClick={handleDownload}
          disabled={!data?.members?.length}
        >
          Download CSV
        </Button>
        <Search
          className="wild-club-header-search"
          placeholder="Search members..."
          allowClear
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          disabled={!data?.members}
        />
        <Tooltip title={!searchQuery ? 'There is no search term' : null}>
          <Button
            type="primary"
            icon={<CloseOutlined />}
            onClick={() => setSearchQuery('')}
            disabled={!searchQuery}
          >
            Clear search
          </Button>
        </Tooltip>
        <div className="wild-club-refresh-actions">
          <Tooltip title="Reload insights from Recharge + Sanity">
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined spin={loading} />}
              onClick={() => fetchData({ refresh: true })}
              disabled={loading}
            >
              Refresh
            </Button>
          </Tooltip>
        </div>
        {downloadCsv && data?.members && (
          <CSVDownload
            data={data.members}
            headers={[
              { label: 'Name', key: 'customerName' },
              { label: 'Email', key: 'customerEmail' },
              { label: 'Status', key: 'subscriptionStatus' },
              { label: 'Joined', key: 'createdAt' },
              { label: 'Boxes sent', key: 'boxesSent' },
              { label: 'Boxes remaining', key: 'boxesRemaining' },
              { label: 'Next box', key: 'nextBoxLabel' },
              { label: 'Next release', key: 'nextReleaseTitle' },
              { label: 'On hold', key: 'onHold' },
              { label: 'Staff', key: 'isStaff' },
              { label: 'Customer ID', key: 'customerId' },
              { label: 'Subscription ID', key: 'subscriptionId' },
              { label: 'Recharge URL', key: 'rechargeUrl' },
              { label: 'Shopify URL', key: 'shopifyUrl' },
            ]}
            target="_blank"
            rel="noopener noreferrer"
          />
        )}
      </StickyHeader>

      <div id="wild-club-body">
        <div id="wild-club-metrics-container">
          <MetricStat
            label="Active members"
            icon={<TeamOutlined />}
            loading={loading}
            value={formatNumber(metrics.activeMembers)}
          />
          <MetricStat
            label={newMembersLabel}
            icon={<UserAddOutlined />}
            loading={loading}
            value={formatNumber(metrics.newMembersThisMonth)}
          />
          <MetricStat
            label="Cancelled / expired"
            icon={<StopOutlined />}
            loading={loading}
            value={formatNumber(metrics.cancelledOrExpired)}
          />
          <MetricStat
            label="Boxes remaining"
            icon={<InboxOutlined />}
            loading={loading}
            value={formatNumber(metrics.boxesRemaining)}
          />
          <MetricStat
            label="Boxes sent"
            icon={<CheckCircleOutlined />}
            loading={loading}
            value={formatNumber(metrics.boxesSent)}
          />
          <MetricStat
            label="On Hold"
            icon={<PauseCircleOutlined />}
            loading={loading}
            value={formatNumber(metrics.onHoldCount)}
          />
          <MetricStat
            label="Total sales"
            icon={<DollarOutlined />}
            loading={loading}
            value={formatCurrency(
              (metrics.totalMembers ?? metrics.activeMembers ?? 0) * BOX_PRICE,
            )}
          />
          <MetricStat
            label="Next confirmed release"
            icon={<CalendarOutlined />}
            loading={loading}
            value={nextConfirmedLabel}
          />
        </div>

        <div id="wild-club-panels">
          <div className="wild-club-panel progress-mix-panel">
            <div className="wild-club-panel-title">Progress mix</div>
            {loading && !progressMix && (
              <div className="wild-club-panel-loading">
                <CustomSpinner fontSize={18} />
              </div>
            )}
            {progressMix && (
              <>
                <div className="progress-mix-summary">
                  <Tag color="blue">{`${formatNumber(metrics.totalMembers)} total`}</Tag>
                  <Tag color="geekblue">{`${formatNumber(progressMix.box1)} on Box 1`}</Tag>
                  <Tag color="cyan">{`${formatNumber(progressMix.box2)} on Box 2`}</Tag>
                  <Tag color="purple">{`${formatNumber(progressMix.box3)} on Box 3`}</Tag>
                  <Tag color="green">{`${formatNumber(progressMix.completed)} completed`}</Tag>
                </div>
                <Table
                  className="progress-mix-table"
                  pagination={false}
                  size="small"
                  dataSource={[
                    { key: 'box1', stage: 'Box 1 (current)', count: progressMix.box1 },
                    { key: 'box2', stage: 'Box 2 (current)', count: progressMix.box2 },
                    { key: 'box3', stage: 'Box 3 (current)', count: progressMix.box3 },
                    { key: 'completed', stage: 'All 3 sent', count: progressMix.completed },
                  ]}
                >
                  <Column title="Stage" dataIndex="stage" key="stage" />
                  <Column
                    title="Members"
                    dataIndex="count"
                    key="count"
                    width={90}
                    align="right"
                    render={(val) => formatNumber(val)}
                  />
                </Table>
              </>
            )}
          </div>

          <div className="wild-club-panel releases-panel">
            <div className="releases-panel-header">
              <div className="wild-club-panel-title">Upcoming releases</div>
              {canOpenDates && (
                <Button
                  size="small"
                  type="link"
                  onClick={() => history.push('/dashboard/webstore/wild-club-date-roll')}
                >
                  Open Dates to roll
                </Button>
              )}
            </div>
            {loading && releaseRows.length === 0 && (
              <div className="wild-club-panel-loading">
                <CustomSpinner fontSize={18} />
              </div>
            )}
            {!loading && releaseRows.length === 0 && (
              <p className="wild-club-panel-empty">No upcoming Sanity releases.</p>
            )}
            {releaseRows.length > 0 && (
              <Table
                dataSource={releaseRows}
                pagination={false}
                size="small"
                columns={[
                  {
                    title: 'Release',
                    dataIndex: 'title',
                    render: (v, record) => (record.isDateTbc ? 'Date TBC' : v),
                  },
                  {
                    title: 'Status',
                    dataIndex: 'scheduleStatus',
                    width: 110,
                    render: (v) => (
                      v === 'confirmed'
                        ? <Tag color="success">Confirmed</Tag>
                        : <Tag color="warning">TBC</Tag>
                    ),
                  },
                  {
                    title: 'Date',
                    dataIndex: 'dateLabel',
                    width: 180,
                  },
                  {
                    title: 'Estimated',
                    dataIndex: 'estimatedOrders',
                    width: 100,
                    render: (v) => <Tag color="blue">{v}</Tag>,
                  },
                  {
                    title: 'On Hold',
                    dataIndex: 'onHoldCount',
                    width: 90,
                    render: (v) => (v > 0 ? <Tag color="orange">{v}</Tag> : <Tag>0</Tag>),
                  },
                ]}
              />
            )}
          </div>
        </div>

        <div className="wild-club-panel members-panel">
          <div className="wild-club-panel-title">Members</div>
          <CustomTable
            id={TABLE_ID}
            pluralLabel="members"
            disableControls
            cacheData={false}
            pagination={{ pageSize: 50 }}
            dataSource={memberRows}
          >
            <Column
              title="Name"
              dataIndex="customerName"
              key="customerName"
              render={(val, record) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {record.rechargeUrl ? (
                    <a href={record.rechargeUrl} target="_blank" rel="noopener noreferrer">
                      {val || record.customerId || '—'}
                    </a>
                  ) : (val || '—')}
                  {record.isStaff && !excludeStaff && (
                    <Tag color="purple">Staff</Tag>
                  )}
                </span>
              )}
            />
            <Column
              title="Email"
              dataIndex="customerEmail"
              key="customerEmail"
              render={(val) => val || '—'}
            />
            <Column
              title="Status"
              dataIndex="subscriptionStatus"
              key="subscriptionStatus"
              width={110}
              render={(val) => (
                <Tag color={statusTagColor(val)}>{val || '—'}</Tag>
              )}
            />
            <Column
              title="Joined"
              dataIndex="createdAt"
              key="createdAt"
              width={140}
              sortType="string"
              defaultSortOrder="descend"
              render={(val) => (
                val && dayjs(val).isValid()
                  ? dayjs(val).format('D MMM YYYY')
                  : '—'
              )}
            />
            <Column
              title="Sent"
              dataIndex="boxesSent"
              key="boxesSent"
              width={70}
              align="right"
              sortType="number"
            />
            <Column
              title="Left"
              dataIndex="boxesRemaining"
              key="boxesRemaining"
              width={70}
              align="right"
              sortType="number"
            />
            <Column
              title="Next release"
              dataIndex="nextReleaseTitle"
              key="nextReleaseTitle"
              render={(val, record) => {
                if (!val) return '—';
                const label = record.nextBoxLabel
                  ? `${record.nextBoxLabel}: ${val}`
                  : val;
                return record.onHold
                  ? (
                    <span>
                      {label}
                      {' '}
                      <Tag color="orange">On Hold</Tag>
                    </span>
                  )
                  : label;
              }}
            />
            <Column
              title="Shopify"
              dataIndex="shopifyUrl"
              key="shopifyUrl"
              width={90}
              render={(val, record) => (
                val ? (
                  <a href={val} target="_blank" rel="noopener noreferrer">
                    {record.shopifyOrderId || 'Order'}
                  </a>
                ) : '—'
              )}
            />
          </CustomTable>
        </div>
      </div>
    </div>
  );
};

export default WildClub;
